import { useEffect, useState } from "react";
import { CalendarDays, Check, ChevronDown, Loader2, MessagesSquare, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/UserAvatar";
import { RichTextEditor, RichTextContent } from "@/components/RichTextEditor";
import { AdjuntoTile, AdjuntosEditor, EnlaceChip, EnlacesEditor, useSubidaAdjuntos } from "@/components/chat/Adjuntos";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { useChat, useNombreUsuario } from "@/contexts/ChatContext";
import { createTrabajo, updateTrabajo, deleteTrabajo, getApiErrorMessage } from "@/lib/api";
import { TRABAJO_ESTADOS, TRABAJO_ESTADO, formatFechaCorta, fechaVencida, formatActividad } from "@/lib/clienteNombre";
import { cn } from "@/lib/utils";

/** Botonera de los 4 estados. */
export function EstadoSelector({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-4 gap-1 rounded-lg bg-muted/60 p-1">
      {TRABAJO_ESTADOS.map((e) => (
        <button
          key={e.key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(e.key)}
          className={cn(
            "flex flex-col items-center gap-1 rounded-md px-1 py-1.5 text-[11px] leading-tight transition",
            value === e.key ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span className={cn("h-2 w-2 rounded-full", e.dot)} />
          {e.label}
        </button>
      ))}
    </div>
  );
}

/** Avatares conmutables de los miembros del chat para asignar. */
export function AsignadosSelector({ miembros, value, onChange }) {
  const { usuariosByEmail } = useChat();
  return (
    <div className="flex flex-wrap gap-1.5">
      {miembros.map((email) => {
        const u = usuariosByEmail[email] || { email, nombre: email.split("@")[0] };
        const on = value.includes(email);
        return (
          <button
            key={email}
            type="button"
            onClick={() => onChange(on ? value.filter((e) => e !== email) : [...value, email])}
            className={cn(
              "flex items-center gap-1.5 rounded-full border pl-0.5 pr-2.5 h-8 text-xs transition",
              on ? "border-primary bg-primary/10 text-foreground" : "border-transparent bg-muted/60 text-muted-foreground hover:text-foreground"
            )}
          >
            <UserAvatar user={u} size={26} />
            {u.nombre}
            {on && <Check className="w-3 h-3 text-primary" />}
          </button>
        );
      })}
    </div>
  );
}

function Etiqueta({ children }) {
  return <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{children}</p>;
}

/** Formulario completo de trabajo (crear o editar). Se guarda con el botón. */
function TrabajoForm({ clienteId, miembros, trabajo, inicial, onSaved, onDeleted, onCancel }) {
  const editando = !!trabajo;
  const [titulo, setTitulo] = useState(trabajo?.titulo ?? inicial?.titulo ?? "");
  const [descripcion, setDescripcion] = useState(trabajo?.descripcion ?? "");
  const [estado, setEstado] = useState(trabajo?.estado ?? "por_empezar");
  const [asignados, setAsignados] = useState(trabajo?.asignados ?? []);
  const [fecha, setFecha] = useState(trabajo?.fecha_limite ?? "");
  const [enlaces, setEnlaces] = useState(trabajo?.enlaces ?? []);
  const [existentes, setExistentes] = useState(trabajo?.adjuntos ?? []);
  const [saving, setSaving] = useState(false);
  const subida = useSubidaAdjuntos(clienteId || trabajo?.cliente_id);

  const guardar = async (e) => {
    e?.preventDefault();
    if (!titulo.trim()) {
      toast.error("Ponle un título al trabajo");
      return;
    }
    if (subida.subiendo) {
      toast.message("Espera a que terminen de subirse los archivos");
      return;
    }
    const payload = {
      titulo: titulo.trim(),
      descripcion,
      estado,
      asignados,
      fecha_limite: fecha || null,
      enlaces: enlaces.map((x) => ({ url: x.url, titulo: x.titulo || "" })),
      adjuntos: [...existentes.map((a) => a.id), ...subida.listos.map((a) => a.id)],
    };
    setSaving(true);
    try {
      const t = editando
        ? await updateTrabajo(trabajo.id, payload)
        : await createTrabajo(clienteId, { ...payload, mensaje_origen_id: inicial?.mensaje_origen_id || null });
      subida.reset();
      onSaved?.(t);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo guardar el trabajo"));
    } finally {
      setSaving(false);
    }
  };

  const borrar = async () => {
    if (!window.confirm("¿Eliminar este trabajo y sus archivos?")) return;
    try {
      await deleteTrabajo(trabajo.id);
      onDeleted?.(trabajo.id);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo borrar"));
    }
  };

  return (
    <form onSubmit={guardar} className="flex flex-col min-h-0">
      <div className="space-y-5 overflow-y-auto px-5 pt-5 pb-4">
        <Input
          autoFocus={!editando}
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="¿Qué hay que hacer?"
          className="h-11 text-base font-medium"
        />
        <EstadoSelector value={estado} onChange={setEstado} />
        <div className="space-y-1.5">
          <Etiqueta>Responsables</Etiqueta>
          <AsignadosSelector miembros={miembros} value={asignados} onChange={setAsignados} />
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <Input type="date" value={fecha || ""} onChange={(e) => setFecha(e.target.value)} className="h-9 w-auto" />
          {fecha && (
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setFecha("")}>
              Quitar fecha
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          <Etiqueta>Información</Etiqueta>
          <RichTextEditor value={descripcion} onChange={setDescripcion} placeholder="Detalles, pasos, textos para el cliente…" minHeight={110} />
        </div>
        <div className="space-y-1.5">
          <Etiqueta>Archivos · hasta 25 MB cada uno</Etiqueta>
          <AdjuntosEditor existentes={existentes} onQuitarExistente={(id) => setExistentes((prev) => prev.filter((a) => a.id !== id))} subida={subida} />
        </div>
        <div className="space-y-1.5">
          <Etiqueta>Enlaces</Etiqueta>
          <EnlacesEditor enlaces={enlaces} onChange={setEnlaces} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t px-5 py-3">
        {editando ? (
          <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive gap-1.5" onClick={borrar}>
            <Trash2 className="w-3.5 h-3.5" /> Eliminar
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
          <Button type="submit" disabled={saving || !titulo.trim()}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editando ? "Guardar" : "Anclar trabajo"}
          </Button>
        </div>
      </div>
    </form>
  );
}

export function NuevoTrabajoDialog({ open, onOpenChange, clienteId, miembros, inicial, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 max-h-[92dvh] flex flex-col overflow-hidden">
        <DialogTitle className="px-5 pt-5 text-base">Nuevo trabajo pendiente</DialogTitle>
        <DialogDescription className="sr-only">Crea un trabajo anclado en el chat del cliente</DialogDescription>
        {open && (
          <TrabajoForm
            clienteId={clienteId}
            miembros={miembros}
            inicial={inicial}
            onSaved={(t) => {
              onSaved?.(t);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Pastilla de estado con menú para cambiarlo en un clic. */
function EstadoPill({ estado, onChange }) {
  const est = TRABAJO_ESTADO[estado] || TRABAJO_ESTADO.por_empezar;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 h-7 text-xs font-medium", est.chip)}>
          <span className={cn("h-2 w-2 rounded-full", est.dot)} />
          {est.label}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {TRABAJO_ESTADOS.map((e) => (
          <DropdownMenuItem key={e.key} onSelect={() => onChange(e.key)}>
            <span className={cn("h-2 w-2 rounded-full mr-2", e.dot)} />
            {e.label}
            {estado === e.key && <Check className="w-3.5 h-3.5 ml-auto" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Detalle de un trabajo, minimalista: estado (cambiable), título, responsables, fecha, información,
 * archivos y enlaces. El lápiz pasa a edición en el mismo modal.
 */
export function TrabajoDetalleDialog({ trabajo, onOpenChange, miembros = [], cliente, onSaved, onDeleted, onIrAlChat }) {
  const { usuariosByEmail } = useChat();
  const nombreDe = useNombreUsuario();
  const [editando, setEditando] = useState(false);
  const open = !!trabajo;

  useEffect(() => {
    if (!open) setEditando(false);
  }, [open]);

  const cambiarEstado = async (estado) => {
    if (estado === trabajo.estado) return;
    try {
      onSaved?.(await updateTrabajo(trabajo.id, { estado }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo cambiar el estado"));
    }
  };

  const vencida = trabajo && trabajo.estado !== "hecho" && fechaVencida(trabajo.fecha_limite);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 max-h-[92dvh] flex flex-col overflow-hidden">
        <DialogDescription className="sr-only">Detalle del trabajo</DialogDescription>
        {trabajo && editando ? (
          <>
            <DialogTitle className="px-5 pt-5 text-base">Editar trabajo</DialogTitle>
            <TrabajoForm
              trabajo={trabajo}
              miembros={miembros}
              onSaved={(t) => {
                onSaved?.(t);
                setEditando(false);
              }}
              onDeleted={(id) => {
                onDeleted?.(id);
                onOpenChange(false);
              }}
              onCancel={() => setEditando(false)}
            />
          </>
        ) : trabajo ? (
          <div className="overflow-y-auto">
            <div className="px-6 pt-5 pb-6 space-y-5">
              <div className="flex items-center gap-2 pr-8">
                <EstadoPill estado={trabajo.estado} onChange={cambiarEstado} />
                <button type="button" onClick={() => setEditando(true)} className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground" title="Editar">
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              <DialogTitle className={cn("text-xl font-semibold leading-snug tracking-tight", trabajo.estado === "hecho" && "line-through text-muted-foreground")}>
                {trabajo.titulo}
              </DialogTitle>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
                {cliente && (
                  <span className="flex items-center gap-2 min-w-0">
                    <ClienteLogo cliente={cliente} size={22} className="rounded-md" />
                    <span className="truncate">{cliente.nombre_visible}</span>
                  </span>
                )}
                {trabajo.asignados?.length > 0 && (
                  <span className="flex items-center gap-2">
                    <span className="flex -space-x-1.5">
                      {trabajo.asignados.map((e) => (
                        <span key={e} className="rounded-full ring-2 ring-background inline-flex">
                          <UserAvatar user={usuariosByEmail[e] || { email: e }} size={24} />
                        </span>
                      ))}
                    </span>
                    <span className="text-muted-foreground">{trabajo.asignados.map(nombreDe).join(", ")}</span>
                  </span>
                )}
                {trabajo.fecha_limite && (
                  <span className={cn("flex items-center gap-1.5", vencida ? "text-destructive font-medium" : "text-muted-foreground")}>
                    <CalendarDays className="w-4 h-4" />
                    {formatFechaCorta(trabajo.fecha_limite)}
                    {vencida && " · vencido"}
                  </span>
                )}
              </div>

              {trabajo.descripcion && <RichTextContent html={trabajo.descripcion} className="text-foreground/90" />}

              {trabajo.adjuntos?.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                  {trabajo.adjuntos.map((a) => (
                    <AdjuntoTile key={a.id} adjunto={a} />
                  ))}
                </div>
              )}

              {trabajo.enlaces?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {trabajo.enlaces.map((e) => (
                    <EnlaceChip key={e.id || e.url} enlace={e} />
                  ))}
                </div>
              )}

              {!trabajo.descripcion && !trabajo.adjuntos?.length && !trabajo.enlaces?.length && (
                <button type="button" onClick={() => setEditando(true)} className="text-sm text-muted-foreground hover:text-foreground">
                  + Añadir información, archivos o enlaces
                </button>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t px-6 py-3 text-[11px] text-muted-foreground">
              <span className="truncate">
                Creado por {nombreDe(trabajo.creado_por)} · {formatActividad(trabajo.fecha_creacion)}
              </span>
              {onIrAlChat && (
                <button type="button" onClick={onIrAlChat} className="flex items-center gap-1.5 shrink-0 font-medium text-foreground hover:underline">
                  <MessagesSquare className="w-3.5 h-3.5" /> Ir al chat
                </button>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
